// ** React Imports
import { Ref, useState, forwardRef, ReactElement, useEffect } from 'react'

// ** MUI Imports
import Box from '@mui/material/Box'
import Tab from '@mui/material/Tab'
import TabList from '@mui/lab/TabList'
import TabPanel from '@mui/lab/TabPanel'
import Avatar from '@mui/material/Avatar'
import Dialog from '@mui/material/Dialog'
import Button from '@mui/material/Button'
import TabContext from '@mui/lab/TabContext'
import IconButton from '@mui/material/IconButton'
import Typography from '@mui/material/Typography'
import Fade, { FadeProps } from '@mui/material/Fade'
import DialogContent from '@mui/material/DialogContent'

// ** Icons Imports
import Close from 'mdi-material-ui/Close'
import Check from 'mdi-material-ui/Check'
import ArrowLeft from 'mdi-material-ui/ArrowLeft'
import ArrowRight from 'mdi-material-ui/ArrowRight'
import ChartDonut from 'mdi-material-ui/ChartDonut'
import StarOutline from 'mdi-material-ui/StarOutline'
import ClipboardOutline from 'mdi-material-ui/ClipboardOutline'

// ** Hook Imports
import { useSettings } from 'src/@core/hooks/useSettings'

// ** Tab Content Imports
import MediumsListingTab from 'src/views/apps/channel/tabs/MediumsListingTab'
import ChannelsTypeSelectionTab from 'src/views/apps/channel/tabs/ChannelsTypeSelectionTab'
import ChannelsSelectionTab from 'src/views/apps/channel/tabs/ChannelsSelectionTab'

import toast from 'react-hot-toast'
import CircularProgress from '@mui/material/CircularProgress';
import { green } from '@mui/material/colors';

import { ChannelType, channelDefault } from 'src/types/apps/channelTypes'
import axios from '../../../../services/ApiService'

import { useDispatch } from 'react-redux'
import { AppDispatch } from 'src/store'
import { setAccessToken } from '../../../../store/apps/lookup'

interface TabLabelProps {
    title: string
    active: boolean
    subtitle: string
    icon: ReactElement
}

const Transition = forwardRef(function Transition(
    props: FadeProps & { children?: ReactElement<any, any> },
    ref: Ref<unknown>
) {
    return <Fade ref={ref} {...props} />
})

const TabLabel = (props: TabLabelProps) => {
    const { icon, title, subtitle, active } = props

    return (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Avatar
                    variant='rounded'
                    sx={{
                        mr: 3.5,
                        ...(active ? { color: 'common.white', backgroundColor: 'primary.main' } : { color: 'text.primary' })
                    }}
                >
                    {icon}
                </Avatar>
                <Box sx={{ textAlign: 'left' }}>
                    <Typography variant='body2'>{title}</Typography>
                    <Typography variant='caption' sx={{ color: 'text.disabled', textTransform: 'none' }}>
                        {subtitle}
                    </Typography>
                </Box>
            </Box>
        </Box>
    )
}

interface DialogProps {
    show: boolean
    setShow: (arg0: boolean) => void
    setMedium: (arg0: number) => void
    setMediumName: (arg0: string) => void
    setChannels: (arg0: ChannelType[]) => void
    setChannelType: (arg0: number) => void
    medium: number
    mediumName : string
    channels: ChannelType[]
    channelType: number
    refreshChannelsData: () => void
    existingChannels: any[]
    activeTab: string
    setActiveTab: (tab: string) => void
}

const tabsArr = ['mediumsTab', 'channelsTypeTab', 'channelsSelectionTab', 'submitTab']

const DialogCreateApp = (props: DialogProps) => {

    const { activeTab, setActiveTab, show, setShow, setMedium,setMediumName, setChannels, setChannelType, medium, mediumName, channels, channelType, refreshChannelsData, existingChannels } = props
    const dispatch = useDispatch<AppDispatch>();

    // ** States
    //const [activeTab, setActiveTab] = useState<string>('mediumsTab')
    const [loading, setLoading] = useState(false);

    // ** Hook
    const { settings } = useSettings()

    // ** Var
    const { direction } = settings

    const handleClose = () => {
        localStorage.removeItem('addChannelWizardState');
        setShow(false)
        setActiveTab('mediumsTab')
    }

    useEffect(() => {
        if (!show) {
            setChannels(channelDefault);
            setMedium(0);
            setMediumName("");
            setChannelType(0);
        }
    }, [show])

    const NextArrow = direction === 'ltr' ? ArrowRight : ArrowLeft
    const PreviousArrow = direction === 'ltr' ? ArrowLeft : ArrowRight

    function createAndSubmitForm(providerName : string) {

        var form = document.createElement('form');
        form.setAttribute('method', 'get');
        form.setAttribute('action', '/Identity/Account/ExternalLogin/ExternalClaimProvider?provider=' + providerName);
        form.style.display = 'none'; // Hide the form to avoid changing the page layout

        var returnUrlInput = document.createElement('input');
        returnUrlInput.setAttribute('type', 'hidden');
        returnUrlInput.setAttribute('name', 'returnUrl');

        const url = new URL(window.location.href);
        url.searchParams.set('modal', 'add-channel');
        returnUrlInput.setAttribute('value', url.pathname + url.search);

        form.appendChild(returnUrlInput);

        var providerInput = document.createElement('input');
        providerInput.setAttribute('type', 'hidden');
        providerInput.setAttribute('name', 'provider');
        providerInput.setAttribute('value', providerName);
        form.appendChild(providerInput);

        document.body.appendChild(form);

        const wizardState = {
            medium,
            mediumName,
            channelType,
            channels,
            activeTab
        };
        localStorage.setItem('addChannelWizardState', JSON.stringify(wizardState));

        form.submit();

        setTimeout(() => form.remove(), 100);
    }


    const renderTabFooter = () => {
        const prevTab = tabsArr[tabsArr.indexOf(activeTab) - 1]
        const nextTab = tabsArr[tabsArr.indexOf(activeTab) + 1]

        const handleFormUpdate = async () => {
            if (activeTab !== 'submitTab') {
                if (activeTab == 'mediumsTab' && !medium) {
                    toast.error('Please select a Platform to continue', {
                        position: 'bottom-right',
                        id: "copy"
                    });
                    return;
                }
                else if (activeTab == 'channelsTypeTab' && !channelType) {
                    toast.error('Please select a Channel Type to continue', {
                        position: 'bottom-right',
                        id: "copy"
                    });
                    return;
                }
                if (activeTab == 'channelsTypeTab') {
                        let claimExist = await axios.get("/Identity/Account/ExternalLogin/CheckExternalClaim?providerName=" + mediumName);
                        if (claimExist.data == false) {
                            createAndSubmitForm(mediumName);
                            return;
                        }
                        else {
                            if (claimExist.data.accessToken) {
                                var accessToken = claimExist.data.accessToken;
                                dispatch(setAccessToken({ providerName: mediumName, accessToken }));
                            }
                        }

                }
                else if (activeTab == 'channelsSelectionTab' && !channels[0].channelId) {
                    toast.error('Please select a Channel to continue', {
                        position: 'bottom-right',
                        id: "copy"
                    });
                    return;
                }
                setActiveTab(nextTab)
            } else {
                setLoading(true);
                let channelsToPost = channels.map((channel) => ({ ...channel, channelTypeId: channelType }))
                await axios.post("channel/SaveChannels", JSON.stringify(channelsToPost));
                toast.success('Channel Added Successfully!');
                setLoading(false);
                handleClose();
                refreshChannelsData();
            }
        };

        return (
            <Box sx={{ mt: 8.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Button
                    variant='outlined'
                    color='secondary'
                    startIcon={<PreviousArrow />}
                    disabled={activeTab === 'mediumsTab'}
                    onClick={() => setActiveTab(prevTab)}
                >
                    Back
                </Button>
                <Button
                    variant='contained'
                    endIcon={activeTab === 'submitTab' ? <Check /> : <NextArrow />}
                    color={activeTab === 'submitTab' ? 'success' : 'primary'}
                    onClick={ handleFormUpdate }
                >
                    {loading && (
                        <CircularProgress
                            size={24}
                            sx={{
                                color: green[500],
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                marginTop: '-12px',
                                marginLeft: '-12px',
                            }}
                        />
                    )}
                    {activeTab === 'submitTab' ? 'Save & Proceed' : 'Next'}
                </Button>
            </Box>
        )
    }

    return (
        <Dialog
            fullWidth
            open={show}
            scroll='body'
            maxWidth='md'
            onClose={handleClose}
            onBackdropClick={handleClose}
            TransitionComponent={Transition}
            style={{ zIndex: 1100 } }
        >
            <DialogContent
                sx={{
                    position: 'relative',
                    pr: { xs: 5, sm: 12 },
                    pl: { xs: 4, sm: 11 },
                    pt: { xs: 8, sm: 12.5 },
                    pb: { xs: 5, sm: 12.5 }
                }}
            >
                <IconButton size='small' onClick={handleClose} sx={{ position: 'absolute', right: '1rem', top: '1rem' }}>
                    <Close />
                </IconButton>
                <Box sx={{ mb: 3, textAlign: 'center' }}>
                    <Typography variant='h5' sx={{ mb: 3, lineHeight: '2rem' }}>
                        Channels
                    </Typography>
                    <Typography variant='body2'>Add Social Media Channels</Typography>
                </Box>
                <Box sx={{ display: 'flex', flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
                    <TabContext value={activeTab}>
                        <TabList
                            orientation='vertical'
                            sx={{
                                border: 0,
                                minWidth: 200,
                                '& .MuiTabs-indicator': { display: 'none' },
                                '& .MuiTabs-flexContainer': {
                                    alignItems: 'flex-start',
                                    '& .MuiTab-root': {
                                        width: '100%',
                                        alignItems: 'flex-start'
                                    }
                                }
                            }}
                        >
                            <Tab
                                disableRipple
                                value='mediumsTab'
                                label={
                                    <TabLabel
                                        title='Platform'
                                        subtitle='Select Social Media Platform'
                                        icon={<ClipboardOutline />}
                                        active={activeTab === 'mediumsTab'}
                                    />
                                }
                            />
                            <Tab
                                disableRipple
                                value='channelsTypeTab'
                                label={
                                    <TabLabel
                                        title='Channel Type'
                                        icon={<StarOutline />}
                                        subtitle='Select a Channel Type'
                                        active={activeTab === 'channelsTypeTab'}
                                    />
                                }
                            />
                            <Tab
                                disableRipple
                                value='channelsSelectionTab'
                                label={
                                    <TabLabel
                                        title='Channels'
                                        active={activeTab === 'channelsSelectionTab'}
                                        subtitle='Select Channel'
                                        icon={<ChartDonut />}
                                    />
                                }
                            />
                            <Tab
                                disableRipple
                                value='submitTab'
                                label={
                                    <TabLabel title='Submit' subtitle='Submit' active={activeTab === 'submitTab'} icon={<Check />} />
                                }
                            />
                        </TabList>
                        <TabPanel value='mediumsTab' sx={{ flexGrow: 1 }}>
                            <MediumsListingTab setMedium={setMedium} medium={medium} setMediumName={setMediumName} mediumName={mediumName} />
                            {renderTabFooter()}
                        </TabPanel>
                        <TabPanel value='channelsTypeTab' sx={{ flexGrow: 1 }}>
                            <ChannelsTypeSelectionTab setChannelType={setChannelType} channelType={channelType} medium={medium} />
                            {renderTabFooter()}
                        </TabPanel>
                        <TabPanel value='channelsSelectionTab' sx={{ flexGrow: 1 }}>
                            <ChannelsSelectionTab channelType={channelType} medium={medium} setChannels={setChannels} channels={channels}
                                existingChannels={existingChannels} />
                                {renderTabFooter()}
                        </TabPanel>
                        <TabPanel value='submitTab' sx={{ flexGrow: 1 }}>
                            <Box
                                sx={{
                                    textAlign: 'center',
                                    padding: 4,
                                    backgroundColor: 'background.default',
                                    borderRadius: 3,
                                    boxShadow: 2,
                                }}
                            >
                                <Typography
                                    variant="h4"
                                    sx={{
                                        marginBottom: 3,
                                        fontWeight: 'bold',
                                        color: 'text.primary',
                                    }}
                                >
                                    Channel Details
                                </Typography>
                                {channels.map((channel, index) => (
                                    <Box
                                        key={index}
                                        sx={{
                                            marginBottom: 4,
                                            padding: 3,
                                            borderRadius: 2,
                                            border: '1px solid',
                                            borderColor: 'divider',
                                            backgroundColor: 'background.paper',
                                            boxShadow: 1,
                                            textAlign: 'left',
                                            display: 'flex',
                                            gap: 2,
                                            alignItems: 'center',
                                        }}
                                    >
                                        <Avatar
                                            src={channel.profileImageUrl}
                                            alt={channel.name}
                                            sx={{
                                                width: 56,
                                                height: 56,
                                                boxShadow: 1,
                                                border: '1px solid',
                                                borderColor: 'divider',
                                            }}
                                        />

                                        <Box sx={{ flexGrow: 1 }}>
                                            <Typography
                                                variant="h6"
                                                sx={{
                                                    fontWeight: 'bold',
                                                    color: 'text.primary',
                                                    marginBottom: 1,
                                                }}
                                            >
                                                {channel.name}
                                            </Typography>

                                            {channel.category && (
                                                <Typography
                                                    variant="body1"
                                                    sx={{
                                                        color: 'text.secondary',
                                                        marginBottom: 1,
                                                    }}
                                                >
                                                    <strong>Category:</strong> {channel.category}
                                                </Typography>
                                            )}

                                            {channel.link && (
                                                <Typography
                                                    variant="body1"
                                                    sx={{
                                                        color: 'text.secondary',
                                                        marginBottom: 1,
                                                    }}
                                                >
                                                    <strong>Channel Link:</strong>{' '}
                                                    <a
                                                        href={
                                                            channel.link ||
                                                            `https://www.facebook.com/groups/${channel.channelId}`
                                                        }
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        style={{
                                                            textDecoration: 'underline',
                                                            color: '#1E88E5',
                                                            fontWeight: 'medium',
                                                        }}
                                                    >
                                                        {channel.name}
                                                    </a>
                                                </Typography>
                                            )}

                                            {(channel.about || channel.description) && (
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        color: 'text.disabled',
                                                        fontStyle: 'italic',
                                                        marginTop: 1,
                                                    }}
                                                >
                                                    <strong>Description:</strong>{' '}
                                                    {channel.about || channel.description}
                                                </Typography>
                                            )}
                                        </Box>
                                    </Box>
                                ))}
                            </Box>


                            {renderTabFooter()}
                        </TabPanel>
                    </TabContext>
                </Box>
            </DialogContent>
        </Dialog>
    )
}

export default DialogCreateApp
